/**
 * 检查项配置（项目管理） - 列表页
 * UI 严格对齐 web后台.html v2 PAGE: 检查项配置（项目视角）
 *
 * 区域：
 *  1. 顶部 header：标题"检查项配置" + 副标题"为项目配置编号及检查项" + [导出][新增项目]
 *  2. 3 KPI：项目数 / 已启用 / 机型编号数
 *  3. 筛选 3 项：关键字(项目名/基地名) / 基地下拉 / 状态下拉 / 重置
 *  4. 表格 8 列：项目名称 / 基地名称 / 整机编号(蓝软Tag 前3+N) / 检查项数量 / 操作人 / 更新时间 / 状态 / 操作(编辑)
 *
 * 数据视角：直接对应接口 mingyang_project.list 返回结构（不再做反向聚合）
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Tooltip,
  message
} from 'antd'
import {
  CameraOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  PlusOutlined,
  ProjectOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons'
import {
  ProjectListItem,
  ProjectListParams,
  ProjectStatsResponse,
  ProjectStatus,
  fetchProjectFilterOptions,
  fetchProjectList,
  fetchProjectStats,
  startProjectExport,
  toggleProjectStatus
} from '../api/project'
import { useIsAdmin } from '../../../app/hooks'
import { selectGroupAndUser } from '../api/client'
import StatusTag from '../shared/StatusTag'
import ExportModal from '../shared/ExportModal'
import ConfigWizard from './ConfigWizard'

const { Option } = Select

interface ListProps {
  dispatch: (...args: any[]) => any
  state: any
  /** 跳转到下载管理页 */
  onGoToDownloads?: () => void
}

interface FilterState {
  q: string
  base: string
  status: '' | 'active' | 'inactive'
}

const initialFilters: FilterState = { q: '', base: '', status: '' }

const ProjectList: React.FC<ListProps> = ({
  dispatch,
  state,
  onGoToDownloads
}) => {
  // 权限：普通成员不可见 新增项目 按钮 + 操作列；管理员/主管理员可见
  const isAdmin = useIsAdmin()
  const [filters, setFilters] = useState<FilterState>(initialFilters)
  const [list, setList] = useState<ProjectListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState<ProjectStatsResponse>({
    total_projects: 0,
    active_projects: 0,
    total_machine_numbers: 0
  })
  const [statsLoading, setStatsLoading] = useState(false)
  const [baseOptions, setBaseOptions] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [exportOpen, setExportOpen] = useState(false)
  const [editing, setEditing] = useState<ProjectListItem | 'new' | null>(null)

  const { user_id, group_id } = selectGroupAndUser(state)

  // 列表入参（关键字 + 基地 + 状态前端二次过滤；接口当前仅支持精确 base/status）
  const apiParams: ProjectListParams = useMemo(
    () => ({
      project_name: filters.q || '',
      base_name: filters.base || '',
      status:
        filters.status === 'active'
          ? 1
          : filters.status === 'inactive'
            ? 0
            : null,
      page,
      page_size: pageSize
    }),
    [filters, page, pageSize]
  )

  const loadList = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchProjectList(dispatch, apiParams)
      setList(data.list || [])
      setTotal(data.total || 0)
    } catch (e) {
      message.error((e as Error)?.message || '加载项目列表失败')
    } finally {
      setLoading(false)
    }
  }, [dispatch, apiParams])

  const loadStats = useCallback(async () => {
    setStatsLoading(true)
    try {
      const s = await fetchProjectStats(dispatch)
      setStats(s)
    } catch (e) {
      console.warn('[project] 统计加载失败:', e)
    } finally {
      setStatsLoading(false)
    }
  }, [dispatch])

  const loadFilterOptions = useCallback(async () => {
    try {
      const data = await fetchProjectFilterOptions(dispatch)
      setBaseOptions(data.base_name_options || [])
    } catch (e) {
      console.warn('[project] 筛选选项加载失败:', e)
    }
  }, [dispatch])

  useEffect(() => {
    loadStats()
    loadFilterOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadList()
  }, [loadList])

  const refreshAll = useCallback(() => {
    loadList()
    loadStats()
  }, [loadList, loadStats])

  const handleReset = useCallback(() => {
    setFilters(initialFilters)
    setPage(1)
  }, [])

  // 停用/启用：Modal 确认 → toggleProjectStatus → 刷新
  const handleToggleStatus = useCallback(
    (row: ProjectListItem) => {
      if (!group_id || !user_id) {
        message.error('未获取到团队或用户信息')
        return
      }
      const target: ProjectStatus = row.status === 1 ? 0 : 1
      const isDeact = target === 0
      Modal.confirm({
        title: `确认${isDeact ? '停用' : '启用'}该项目？`,
        content: (
          <div style={{ marginTop: 8, color: '#6B7280', lineHeight: 1.6 }}>
            &quot;<b style={{ color: '#1F2937' }}>{row.project_name}</b>&quot;
            {isDeact
              ? ' 停用后将不再下发到采集端，已采集的历史记录不受影响。'
              : ' 启用后将立即纳入采集流程。'}
          </div>
        ),
        okText: `确认${isDeact ? '停用' : '启用'}`,
        okType: isDeact ? 'danger' : 'primary',
        cancelText: '取消',
        onOk: async () => {
          try {
            await toggleProjectStatus(dispatch, {
              project_id: row.project_id,
              status: target,
              group_id,
              user_id
            })
            message.success(isDeact ? '已停用' : '已启用')
            refreshAll()
          } catch (e) {
            message.error((e as Error)?.message || '操作失败')
          }
        }
      })
    },
    [dispatch, group_id, user_id, refreshAll]
  )

  const handleExport = useCallback(async () => {
    if (!group_id || !user_id) {
      message.error('未获取到团队或用户信息')
      return
    }
    try {
      await startProjectExport(dispatch, {
        project_name: filters.q || '',
        base_name: filters.base || '',
        status:
          filters.status === 'active'
            ? 1
            : filters.status === 'inactive'
              ? 0
              : null,
        group_id,
        user_id
      })
      setExportOpen(true)
    } catch (e) {
      message.error((e as Error)?.message || '导出失败')
    }
  }, [dispatch, filters, group_id, user_id])

  const columns = useMemo(
    () => [
      {
        title: '项目名称',
        dataIndex: 'project_name',
        key: 'project_name',
        width: 160,
        ellipsis: true,
        render: (text: string) => (
          <Tooltip title={text}>
            <span style={{ color: '#1F2937', fontWeight: 500 }}>
              {text || '-'}
            </span>
          </Tooltip>
        )
      },
      {
        title: '基地名称',
        dataIndex: 'base_name',
        key: 'base_name',
        width: 120,
        ellipsis: true
      },
      {
        title: '整机编号',
        dataIndex: 'project_numbers',
        key: 'project_numbers',
        width: 200,
        render: (text: string) => {
          const codes = (text || '')
            .split(',')
            .map((c) => c.trim())
            .filter(Boolean)
          const visible = codes.slice(0, 3)
          const rest = codes.length - visible.length
          if (codes.length === 0)
            return <span style={{ color: '#D9D9D9' }}>-</span>
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {visible.map((c) => (
                <span
                  key={c}
                  className="myse-soft-tag myse-soft-tag--blue"
                  style={{ fontFamily: 'Inter, system-ui' }}
                >
                  {c}
                </span>
              ))}
              {rest > 0 && (
                <Tooltip title={codes.slice(3).join('、')}>
                  <span className="myse-soft-tag myse-soft-tag--slate">
                    +{rest}
                  </span>
                </Tooltip>
              )}
            </div>
          )
        }
      },
      {
        title: '检查项数量',
        dataIndex: 'knowledge_count',
        key: 'knowledge_count',
        width: 100,
        render: (n: number) => (
          <span>
            <span style={{ fontWeight: 600, color: '#1F2937' }}>{n ?? 0}</span>
            <span style={{ color: '#9CA3AF', marginLeft: 4 }}>项</span>
          </span>
        )
      },
      {
        title: '操作人',
        dataIndex: 'operator_name',
        key: 'operator_name',
        width: 80
      },
      {
        title: '更新时间',
        dataIndex: 'update_time',
        key: 'update_time',
        width: 150
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 70,
        render: (s: ProjectStatus) =>
          s === 1 ? (
            <StatusTag label="启用" color="#10A66B" bg="#E7F8EF" />
          ) : (
            <StatusTag label="停用" color="#9CA3AF" bg="#F1F2F4" />
          )
      },
      // 操作列：仅 isAdmin 显示；普通成员看不到 编辑/启停 入口
      ...(isAdmin
        ? [
            {
              title: '操作',
              key: 'action',
              width: 120,
              fixed: 'right' as const,
              render: (_: any, row: ProjectListItem) => (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 12
                  }}
                >
                  <a
                    className="myse-link myse-link--nav"
                    onClick={() => setEditing(row)}
                  >
                    编辑
                  </a>
                  <a
                    className={
                      'myse-link ' +
                      (row.status === 1
                        ? 'myse-link--danger'
                        : 'myse-link--success')
                    }
                    onClick={() => handleToggleStatus(row)}
                  >
                    {row.status === 1 ? '停用' : '启用'}
                  </a>
                </span>
              )
            }
          ]
        : [])
    ],
    [handleToggleStatus, isAdmin]
  )

  return (
    <div className="project-list">
      {/* Header */}
      <div className="myse-page-header">
        <div>
          <h1 className="myse-page-title">检查项配置</h1>
          <div className="myse-page-subtitle">为项目配置编号及检查项</div>
        </div>
        <div className="myse-page-header__actions">
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={!group_id || !user_id || total === 0}
          >
            导出
          </Button>
          {isAdmin && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setEditing('new')}
            >
              新增项目
            </Button>
          )}
        </div>
      </div>

      {/* 3 KPI */}
      <Spin spinning={statsLoading} delay={120}>
        <div className="mingyang-stats mingyang-stats--cols-3">
          <KpiCard
            icon={<ProjectOutlined />}
            label="项目数"
            value={stats.total_projects}
            color="#3B7DEB"
            bg="#E9F2FF"
          />
          <KpiCard
            icon={<CheckCircleOutlined />}
            label="已启用"
            value={stats.active_projects}
            color="#10A66B"
            bg="#E7F8EF"
          />
          <KpiCard
            icon={<CameraOutlined />}
            label="机型编号数"
            value={stats.total_machine_numbers}
            color="#7B5BEB"
            bg="#F1EBFF"
          />
        </div>
      </Spin>

      {/* 筛选区 4 列：q / 基地 / 状态 / 重置 */}
      <div
        className="mingyang-filterbar"
        style={{
          display: 'grid',
          gridTemplateColumns: '1.6fr 1fr 1fr auto',
          gap: 10,
          alignItems: 'center'
        }}
      >
        <Input
          value={filters.q}
          onChange={(e) => {
            // React 16 合成事件池：函数式 setState 异步执行前 e.target 可能被回收
            const v = e.target.value
            setFilters((f) => ({ ...f, q: v }))
            setPage(1)
          }}
          placeholder="项目名称 / 基地名称"
          prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
          allowClear
        />
        <Select
          value={filters.base || undefined}
          onChange={(v: string) => {
            setFilters((f) => ({ ...f, base: v || '' }))
            setPage(1)
          }}
          placeholder="基地名称"
          allowClear
          showSearch
        >
          {baseOptions.map((b) => (
            <Option key={b} value={b}>
              {b}
            </Option>
          ))}
        </Select>
        <Select
          value={filters.status || undefined}
          onChange={(v: 'active' | 'inactive' | undefined) => {
            setFilters((f) => ({ ...f, status: v || '' }))
            setPage(1)
          }}
          placeholder="状态"
          allowClear
        >
          <Option value="active">启用</Option>
          <Option value="inactive">停用</Option>
        </Select>
        <a className="myse-reset-btn" onClick={handleReset}>
          <ReloadOutlined />
          重置
        </a>
      </div>

      {/* 表格 */}
      <div className="mingyang-table-wrapper">
        <Table
          rowKey="project_id"
          loading={loading}
          columns={columns}
          dataSource={list}
          scroll={{ x: 1100 }}
          pagination={false}
          size="middle"
        />
        <div className="mingyang-table-footer">
          <span>共 {total} 条</span>
        </div>
      </div>

      {/* 新增/编辑两步向导 */}
      {editing && (
        <ConfigWizard
          row={editing === 'new' ? null : editing}
          dispatch={dispatch}
          state={state}
          onClose={() => setEditing(null)}
          onSuccess={() => {
            setEditing(null)
            refreshAll()
          }}
        />
      )}

      {/* 导出提示 */}
      <ExportModal
        visible={exportOpen}
        onClose={() => setExportOpen(false)}
        onGoToDownloads={onGoToDownloads}
      />
    </div>
  )
}

const KpiCard: React.FC<{
  icon: React.ReactNode
  label: string
  value: number
  color: string
  bg: string
}> = ({ icon, label, value, color, bg }) => (
  <div className="mingyang-stats__card">
    <div className="mingyang-stats__icon" style={{ background: bg, color }}>
      {icon}
    </div>
    <div className="mingyang-stats__content">
      <div className="mingyang-stats__value" style={{ color: '#1F2937' }}>
        {value}
      </div>
      <div className="mingyang-stats__label">{label}</div>
    </div>
  </div>
)

export default ProjectList
